import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

type TPartTimeValidationContextType = {
  /** Register a time-order error for one workshop part, or clear it with null. */
  reportPartTimeError: (workshopPartId: string, error: string | null) => void;
  /** True while any mounted part editor holds an unsaved invalid start/end pair. */
  hasPartTimeErrors: boolean;
};

const noop = () => undefined;

/**
 * Default value keeps consumers renderable without a provider — PartDateInput and
 * the action buttons are each mounted bare in their own specs.
 */
export const PartTimeValidationContext =
  createContext<TPartTimeValidationContextType>({
    reportPartTimeError: noop,
    hasPartTimeErrors: false,
  });

/**
 * Shares "a part editor is showing an End <= Start error" between the Details tab
 * and the Publish/Republish buttons.
 *
 * PartDateInput deliberately does not persist an invalid time, so the `data.parts`
 * the buttons read from the server still looks valid and their own ordering check
 * cannot see the problem — which is exactly how GROW-4212 was reported (invalid
 * time on screen, Publish still enabled). This carries that transient state across
 * the tree: Actions is a sibling of Tabs, and DetailsTab renders under
 * `destroyInactiveTabPane`, so prop lifting cannot reach it.
 *
 * Leaving the Details tab unmounts the editors and clears their entries. The
 * unsaved value is discarded at the same moment, so the UI and the server agree
 * again and the buttons correctly re-enable.
 */
export const PartTimeValidationProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [errors, setErrors] = useState<Record<string, string | null>>({});

  const reportPartTimeError = useCallback(
    (workshopPartId: string, error: string | null) => {
      setErrors(prev => {
        // Skip the write when nothing changed — PartDateInput reports from an
        // effect that re-runs on every part/time change.
        if ((prev[workshopPartId] ?? null) === error) return prev;
        return { ...prev, [workshopPartId]: error };
      });
    },
    [],
  );

  const hasPartTimeErrors = useMemo(
    () => Object.values(errors).some(Boolean),
    [errors],
  );

  const value = useMemo(
    () => ({ reportPartTimeError, hasPartTimeErrors }),
    [reportPartTimeError, hasPartTimeErrors],
  );

  return (
    <PartTimeValidationContext.Provider value={value}>
      {children}
    </PartTimeValidationContext.Provider>
  );
};

export const useReportPartTimeError = () =>
  useContext(PartTimeValidationContext).reportPartTimeError;

export const useHasPartTimeErrors = () =>
  useContext(PartTimeValidationContext).hasPartTimeErrors;
